import React, { useState, useEffect } from 'react';

const initialFormState = {
    nama_poli: '',
    jumlah_dokter: 0,
    jam_operasional: '',
    status: 'aktif',
    deskripsi: ''
};

const PoliModal = ({ isOpen, onClose, onSubmit, editData = null, isSubmitting = false }) => {
    const [formData, setFormData] = useState({
        ...initialFormState
    });

    useEffect(() => {
        if (editData) {
            setFormData({
                nama_poli: editData.nama_poli || '',
                jumlah_dokter: editData.jumlah_dokter ?? (editData.dokter?.length || 0),
                jam_operasional: editData.jam_operasional || '',
                status: editData.status || 'aktif',
                deskripsi: editData.deskripsi || ''
            });
        } else {
            setFormData(initialFormState);
        }
    }, [editData, isOpen]);

    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    const handleBackdropClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: name === 'jumlah_dokter' ? (value === '' ? '' : Number(value)) : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        await onSubmit(formData);
    };

    return (
        <div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm transition-opacity"
            onClick={handleBackdropClick}
        >
            <div
                className="bg-white w-full max-w-[550px] rounded-[32px] shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 max-h-[90vh] overflow-y-auto"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                <form onSubmit={handleSubmit} className="p-8">
                    {/* Header */}
                    <div className="flex justify-between items-center mb-8">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {editData ? 'Edit Poli' : 'Tambah Poli'}
                        </h2>
                        <button
                            type="button"
                            onClick={onClose}
                            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#06B6D4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <line x1="18" y1="6" x2="6" y2="18"></line>
                                <line x1="6" y1="6" x2="18" y2="18"></line>
                            </svg>
                        </button>
                    </div>

                    <div className="space-y-5">
                        {/* Nama Poli */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Nama Poli</label>
                            <input
                                type="text"
                                name="nama_poli"
                                value={formData.nama_poli}
                                onChange={handleInputChange}
                                placeholder="Contoh: Poli Umum"
                                className="w-full h-[54px] px-4 bg-white border border-cyan-100 rounded-2xl 
                                focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 
                                text-gray-700 font-body transition-all placeholder:text-gray-400"
                                required
                            />
                        </div>

                        {/* Deskripsi */}
                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Deskripsi</label>
                            <textarea
                                name="deskripsi"
                                value={formData.deskripsi}
                                onChange={handleInputChange}
                                placeholder="Deskripsi singkat tentang poli..."
                                rows={3}
                                className="w-full px-4 py-3 bg-white border border-cyan-100 rounded-2xl 
                                focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 
                                text-gray-700 font-body transition-all placeholder:text-gray-400 resize-none"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Jumlah Dokter</label>
                            <input
                                type="number"
                                min="0"
                                name="jumlah_dokter"
                                value={formData.jumlah_dokter}
                                onChange={handleInputChange}
                                placeholder="Contoh: 4"
                                className="w-full h-[54px] px-4 bg-white border border-cyan-100 rounded-2xl 
                                focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 
                                text-gray-700 font-body transition-all placeholder:text-gray-400"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-2">Jam Operasional</label>
                            <input
                                type="text"
                                name="jam_operasional"
                                value={formData.jam_operasional}
                                onChange={handleInputChange}
                                placeholder="Contoh: 08:00 - 16:00"
                                className="w-full h-[54px] px-4 bg-white border border-cyan-100 rounded-2xl 
                                focus:outline-none focus:ring-2 focus:ring-cyan-500/20 focus:border-cyan-500 
                                text-gray-700 font-body transition-all placeholder:text-gray-400"
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-bold text-gray-700 mb-3">Status</label>
                            <div className="flex gap-4">
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, status: 'aktif' }))}
                                    className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${formData.status === 'aktif'
                                        ? 'bg-[#0DBC94] text-white shadow-lg shadow-green-500/30'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                        }`}
                                >
                                    Aktif
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setFormData(prev => ({ ...prev, status: 'nonaktif' }))}
                                    className={`flex-1 py-3 rounded-xl font-bold text-sm transition-all ${formData.status === 'nonaktif'
                                        ? 'bg-[#F64E60] text-white shadow-lg shadow-red-500/30'
                                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                                        }`}
                                >
                                    Non-Aktif
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full py-4 mt-8 bg-gradient-to-r from-[#06B6D4] to-[#038DA5] text-white 
                        font-bold rounded-2xl hover:opacity-90 transition-all shadow-lg shadow-cyan-500/30 
                        active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? 'Menyimpan...' : editData ? 'Simpan Perubahan' : 'Tambah Poli'}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default PoliModal;
